import { useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Columns3, Filter } from 'lucide-react';
import { UI_MARKETS, type MarketCode, type SubSegment } from '@hc/shared';
import { PanelFrame } from '../panel-frame.js';
import { Badge } from '../badge.js';
import { useActiveCountry, type PanelId, type TabId } from '../store.js';
import { listOperatingPressure, type OperatingPressureRow } from '../../api/client.js';
import {
  OpPressureFilterModal,
  type SuperCategory,
  type SeverityKey,
  type DirectionKey,
} from '../op-pressure-filter-modal.js';
import { ColumnsMenu } from '../columns-menu.js';
import { ColumnHeaderCell } from '../column-header-cell.js';
import {
  usePressureColumns,
  PRESSURE_COLUMN_DEFS,
  type PressureColumnId,
  type PressureColumnConfig,
  type PressureColumnActions,
} from '../pressure-columns.js';

// Operating Pressure panel — v1.1 Screener-style surface.
// One unified table; eight columns; one row per event (no expansion).
// Six-filter bar at the top mirrors brief §12.3.

const STALE_MS = 5 * 60_000;
const FETCH_LIMIT = 200;

// ---------- Filter taxonomy --------------------------------------------

const SUPER_CATEGORY_LABEL: Record<SuperCategory, string> = {
  regulatory: 'Regulatory',
  financial: 'Financial',
  structural: 'Structural',
};

type TimeRangeKey = '24h' | '7d' | '30d' | '90d' | 'all';

const TIME_RANGE_DAYS: Record<TimeRangeKey, number | null> = {
  '24h': 1,
  '7d': 7,
  '30d': 30,
  '90d': 90,
  all: null,
};

const TIME_RANGE_ORDER: readonly TimeRangeKey[] = ['24h', '7d', '30d', '90d', 'all'];

// ---------- Component --------------------------------------------------

export function OperatingPressurePanel({ tabId, panelId }: { tabId: TabId; panelId: PanelId }) {
  const { t } = useTranslation();
  const workspaceCountry = useActiveCountry();

  // Defaults per brief §12.5: 30-day window; "My markets only"; all
  // other filters open. Severity / direction unfiltered so easing
  // pressures appear alongside rising — useful context.
  const [timeRange, setTimeRange] = useState<TimeRangeKey>('30d');
  // Default on first load: "All Europe" — empty country set = no
  // filter, all events pass. The panel intentionally does NOT follow
  // the workspace-level country picker because operating pressure is
  // a Europe-wide reference surface, not an entity-keyed view; cross-
  // border events affect multiple markets, and the user should opt in
  // to narrowing via the Filters modal rather than have it forced
  // implicitly. (Brief §12.5 suggests "My markets only" as the
  // default; the operator override here is to default to All Europe
  // until per-user launch-market preferences are wired up — see
  // panel TODOs #4.)
  void workspaceCountry;
  const [countries, setCountries] = useState<Set<MarketCode>>(() => new Set<MarketCode>());
  const [subSegments, setSubSegments] = useState<Set<SubSegment>>(new Set());
  const [superCats, setSuperCats] = useState<Set<SuperCategory>>(new Set());
  const [severities, setSeverities] = useState<Set<SeverityKey>>(new Set());
  const [directions, setDirections] = useState<Set<DirectionKey>>(new Set());
  const [filterModalOpen, setFilterModalOpen] = useState(false);

  // Column layout (order / width / visibility), persisted per panel, shared by
  // the toolbar "Columns" manager and the table headers.
  const [cols, colActions] = usePressureColumns('corryk:pressure-columns:v1');
  const [columnsOpen, setColumnsOpen] = useState(false);
  const columnsBtnRef = useRef<HTMLButtonElement>(null);
  // Column sort — defaults to newest-first (date desc); click a header to
  // re-sort a-z / z-a like Screener.
  const [sortKey, setSortKey] = useState<PressureColumnId>('date');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const { data, isLoading, isError } = useQuery({
    queryKey: ['operating-pressure', { limit: FETCH_LIMIT }],
    queryFn: () => listOperatingPressure({ limit: FETCH_LIMIT }),
    staleTime: STALE_MS,
  });

  const allRows = data?.data ?? [];

  const filteredRows = useMemo(() => {
    const cutoffDays = TIME_RANGE_DAYS[timeRange];
    const cutoff = cutoffDays != null ? Date.now() - cutoffDays * 86_400_000 : null;

    return allRows.filter((r) => {
      // Time filter — based on event_date (brief §3.1).
      if (cutoff !== null) {
        const t = r.eventDate ? Date.parse(r.eventDate) : NaN;
        if (Number.isFinite(t) && t < cutoff) return false;
        // Rows with no date pass — better to surface than hide.
      }
      // Country filter — pass if ANY affected country matches.
      if (countries.size > 0) {
        const hit = r.affectedCountries.some((c) => countries.has(c as MarketCode));
        if (!hit && r.affectedCountries.length > 0) return false;
        // Multi-EU events with no specific countries listed always pass.
      }
      // Sub-segment filter — pass if ANY listed segment matches.
      if (subSegments.size > 0) {
        const hit = r.affectedSubSegments.some((s) => subSegments.has(s as SubSegment));
        if (!hit) return false;
      }
      // Super-category filter — direct field, no legacy mapping.
      if (superCats.size > 0) {
        if (!superCats.has(r.pressureCategory)) return false;
      }
      if (severities.size > 0) {
        if (!severities.has(r.severity)) return false;
      }
      if (directions.size > 0) {
        if (!directions.has(r.direction)) return false;
      }
      return true;
    });
  }, [allRows, timeRange, countries, subSegments, superCats, severities, directions]);

  const sortedRows = useMemo(() => {
    return [...filteredRows].sort((a, b) =>
      cmpPressure(pressureSortValue(sortKey, a), pressureSortValue(sortKey, b), sortDir),
    );
  }, [filteredRows, sortKey, sortDir]);

  function onSort(k: PressureColumnId) {
    if (k === sortKey) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(k);
      // date + severity carry a natural high→low default; text columns a-z.
      setSortDir(k === 'date' || k === 'severity' ? 'desc' : 'asc');
    }
  }

  function clearAll() {
    setTimeRange('30d');
    setCountries(new Set()); // back to "All Europe"
    setSubSegments(new Set());
    setSuperCats(new Set());
    setSeverities(new Set());
    setDirections(new Set());
  }

  function setAllCountriesEuropean() {
    // "All Europe" — clear the filter so any affected_country passes.
    setCountries(new Set());
  }

  function setMyMarketsOnly() {
    setCountries(new Set(UI_MARKETS));
  }

  // Default state: countries empty (All Europe), subSegments empty,
  // superCats empty, severities empty, directions empty, time = 30d.
  // Anything that diverges from that adds to the active count.
  const activeFilterCount =
    (timeRange !== '30d' ? 1 : 0) +
    (countries.size > 0 ? 1 : 0) +
    (subSegments.size > 0 ? 1 : 0) +
    (superCats.size > 0 ? 1 : 0) +
    (severities.size > 0 ? 1 : 0) +
    (directions.size > 0 ? 1 : 0);

  // The "non-time" filter count drives the badge on the Filter button
  // (Time is a sibling control in the toolbar, so excluded here to keep
  // the button badge meaningful for the modal-scope filters).
  const modalFilterCount =
    (countries.size > 0 ? 1 : 0) +
    (subSegments.size > 0 ? 1 : 0) +
    (superCats.size > 0 ? 1 : 0) +
    (severities.size > 0 ? 1 : 0) +
    (directions.size > 0 ? 1 : 0);

  return (
    <PanelFrame tabId={tabId} panelId={panelId}>
      <div
        style={{
          display: 'grid',
          gridTemplateRows: 'auto 1fr',
          // minmax(0, 1fr) column (not the implicit auto-min) so a table
          // wider than the panel can't force the grid — and therefore the
          // whole panel — to grow. With the floor at 0 the scroll area
          // below stays bounded to the panel width and overflows into its
          // own scrollbar instead of escaping the panel frame.
          gridTemplateColumns: 'minmax(0, 1fr)',
          height: '100%',
          minHeight: 0,
          minWidth: 0,
        }}
      >
        <Toolbar
          t={t}
          timeRange={timeRange}
          setTimeRange={setTimeRange}
          onOpenFilters={() => setFilterModalOpen(true)}
          modalFilterCount={modalFilterCount}
          activeFilterCount={activeFilterCount}
          onClear={clearAll}
          cols={cols}
          colActions={colActions}
          columnsOpen={columnsOpen}
          setColumnsOpen={setColumnsOpen}
          columnsBtnRef={columnsBtnRef}
        />
        <OpPressureFilterModal
          open={filterModalOpen}
          onOpenChange={setFilterModalOpen}
          countries={countries}
          setCountries={setCountries}
          onMyMarkets={setMyMarketsOnly}
          onAllEurope={setAllCountriesEuropean}
          subSegments={subSegments}
          setSubSegments={setSubSegments}
          categories={superCats}
          setCategories={setSuperCats}
          severities={severities}
          setSeverities={setSeverities}
          directions={directions}
          setDirections={setDirections}
          resultCount={sortedRows.length}
          totalCount={allRows.length}
          onClearAll={clearAll}
        />
        {/* Two-layer wrapper so the sticky thead has nothing above it
            to scroll through.
              outer (padded, NOT scrolling) — owns the 12px gutter
              inner (overflow:auto, NO padding) — the actual scroll
            Previously the padding was on the scroll container itself.
            Chrome paints scrollable content through the padding region
            on its way past the sticky threshold (top:0 of scrollport),
            which is what produced the "rows appear above the header"
            artefact during vertical scroll. With the padding outside
            the scroll, the scroll bounds are flush; sticky pins at
            the visible top of the scroll area and rows can only scroll
            *under* it. */}
        <div
          style={{
            padding: 'var(--s-3)',
            minHeight: 0,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Bounded viewport: the table sits inside the panel and its
              columns/rows scroll within THIS box (both axes). minWidth:0
              lets it shrink to the panel width so a wide table produces a
              horizontal scrollbar here rather than widening the panel. */}
          <div style={{ flex: 1, minHeight: 0, minWidth: 0, overflow: 'auto' }}>
            {isError ? (
              <EmptyMessage tone="error">{t('panels.lists.error')}</EmptyMessage>
            ) : isLoading ? (
              <EmptyMessage>{t('panelShell.loading')}</EmptyMessage>
            ) : allRows.length === 0 ? (
              <EmptyMessage>{t('panels.landing.awaitingFirstSignal')}</EmptyMessage>
            ) : sortedRows.length === 0 ? (
              <EmptyState
                onClear={clearAll}
                timeRange={timeRange}
                setTimeRange={setTimeRange}
                totalEvents={allRows.length}
              />
            ) : (
              <PressureTable
                rows={sortedRows}
                cols={cols}
                colActions={colActions}
                sortKey={sortKey}
                sortDir={sortDir}
                onSort={onSort}
              />
            )}
          </div>
        </div>
      </div>
    </PanelFrame>
  );
}

// ---------- Toolbar ----------------------------------------------------

interface ToolbarProps {
  t: ReturnType<typeof useTranslation>['t'];
  timeRange: TimeRangeKey;
  setTimeRange: (k: TimeRangeKey) => void;
  onOpenFilters: () => void;
  /** Modal-scope active filter count — drives the badge on the Filter
   *  button (excludes time, which has its own dropdown control). */
  modalFilterCount: number;
  /** Total active filters across modal + time, used to show the inline
   *  Clear button. */
  activeFilterCount: number;
  onClear: () => void;
  cols: PressureColumnConfig;
  colActions: PressureColumnActions;
  columnsOpen: boolean;
  setColumnsOpen: (v: boolean) => void;
  columnsBtnRef: React.RefObject<HTMLButtonElement | null>;
}

function Toolbar(props: ToolbarProps) {
  const {
    t,
    timeRange,
    setTimeRange,
    onOpenFilters,
    modalFilterCount,
    activeFilterCount,
    onClear,
    cols,
    colActions,
    columnsOpen,
    setColumnsOpen,
    columnsBtnRef,
  } = props;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--s-2)',
        padding: 'var(--s-2) var(--s-3)',
        borderBottom: '1px solid var(--hairline)',
        background: 'var(--surface)',
        // Single-row toolbar — overflow on tight widths goes to a
        // horizontal scroll rather than wrapping (keeps the body height
        // predictable so the table always has room).
        overflowX: 'auto',
        whiteSpace: 'nowrap',
      }}
    >
      {/* Time-range dropdown — native select for accessibility +
          keyboard nav; styled to match the Filters button. */}
      <label
        aria-label={t('panels.opPressure.filters.time')}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 'var(--s-1)',
          fontSize: 'var(--t-body)',
          color: 'var(--text-secondary)',
          flexShrink: 0,
        }}
      >
        <select
          value={timeRange}
          onChange={(e) => setTimeRange(e.target.value as TimeRangeKey)}
          style={{
            fontSize: 'var(--t-body)',
            fontFamily: 'inherit',
            padding: '2px var(--s-1)',
            border: '1px solid var(--hairline)',
            borderRadius: 2,
            background: 'var(--menu-surface)',
            color: 'var(--text-primary)',
            cursor: 'pointer',
          }}
        >
          {TIME_RANGE_ORDER.map((k) => (
            <option key={k} value={k}>
              {t(`panels.opPressure.filters.timeRange.${k}`)}
            </option>
          ))}
        </select>
      </label>

      {/* Filters button — opens the modal with all 5 chip-group filters. */}
      <button
        type="button"
        onClick={onOpenFilters}
        aria-haspopup="dialog"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 'var(--s-1)',
          padding: '2px var(--s-2)',
          fontSize: 'var(--t-body)',
          border: '1px solid var(--hairline)',
          borderRadius: 2,
          background: modalFilterCount > 0 ? 'var(--color-accent-100)' : 'var(--surface)',
          color: 'var(--text-primary)',
          cursor: 'pointer',
          flexShrink: 0,
        }}
      >
        <Filter size={12} />
        <span>{t('panels.opPressure.filters.button')}</span>
        {modalFilterCount > 0 && (
          <span
            data-numeric
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 'var(--t-meta)',
              color: 'var(--accent)',
            }}
          >
            · {modalFilterCount}
          </span>
        )}
      </button>

      {/* Columns manager — show / hide / reorder / resize, persisted per panel. */}
      <button
        ref={columnsBtnRef}
        type="button"
        onClick={() => setColumnsOpen(!columnsOpen)}
        aria-haspopup="menu"
        aria-expanded={columnsOpen}
        title="Show, hide and reorder columns"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 'var(--s-1)',
          padding: '2px var(--s-2)',
          fontSize: 'var(--t-body)',
          border: '1px solid var(--hairline)',
          borderRadius: 2,
          background: 'var(--surface)',
          color: 'var(--text-primary)',
          cursor: 'pointer',
          flexShrink: 0,
        }}
      >
        <Columns3 size={12} />
        <span>Columns</span>
      </button>
      <ColumnsMenu
        open={columnsOpen}
        onClose={() => setColumnsOpen(false)}
        anchorRef={columnsBtnRef}
        columns={cols.columns}
        label={(id) => PRESSURE_COLUMN_DEFS[id].label}
        actions={colActions}
      />

      <div style={{ flex: 1 }} />

      {activeFilterCount > 0 && (
        <button
          type="button"
          onClick={onClear}
          style={{
            fontSize: 'var(--t-meta)',
            padding: '2px var(--s-1)',
            border: '1px solid var(--hairline)',
            borderRadius: 2,
            background: 'transparent',
            color: 'var(--text-secondary)',
            cursor: 'pointer',
            flexShrink: 0,
          }}
        >
          {t('panels.opPressure.filters.clear')}
        </button>
      )}
    </div>
  );
}

// ---------- Table ------------------------------------------------------

function PressureTable({
  rows,
  cols,
  colActions,
  sortKey,
  sortDir,
  onSort,
}: {
  rows: OperatingPressureRow[];
  cols: PressureColumnConfig;
  colActions: PressureColumnActions;
  sortKey: PressureColumnId;
  sortDir: 'asc' | 'desc';
  onSort: (id: PressureColumnId) => void;
}) {
  // Div-grid (not a <table>) mirroring the Screener / Referrals tables, so the
  // columns reorder, resize and hide. Visible columns in saved order drive the
  // grid template + the scroll width; the parent scroll container gives the
  // horizontal scrollbar when the total width exceeds the panel.
  const visibleCols = cols.columns.filter((c) => c.visible);
  const gridTemplate = visibleCols.map((c) => `${c.width}px`).join(' ');
  const gridMinWidth = visibleCols.reduce((sum, c) => sum + c.width, 0);

  return (
    <div className="pr-grid" role="table" style={{ minWidth: gridMinWidth }}>
      <style>{PR_CSS}</style>
      <div className="pr-head" role="row" style={{ gridTemplateColumns: gridTemplate }}>
        {visibleCols.map((c) => (
          <ColumnHeaderCell
            key={c.id}
            col={c}
            columns={cols.columns}
            label={PRESSURE_COLUMN_DEFS[c.id].label}
            numeric={PRESSURE_COLUMN_DEFS[c.id].numeric}
            active={sortKey === c.id}
            sortDir={sortDir}
            onSort={onSort}
            moveColumn={colActions.moveColumn}
            setColumnWidth={colActions.setColumnWidth}
          />
        ))}
      </div>
      {rows.map((r) => (
        <div key={r.id} role="row" className="pr-row" style={{ gridTemplateColumns: gridTemplate }}>
          {visibleCols.map((c) => {
            const { cls, node, title } = renderPressureCell(c.id, r);
            return (
              <div key={c.id} className={cls} title={title}>
                {node}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

function renderPressureCell(
  id: PressureColumnId,
  r: OperatingPressureRow,
): { cls: string; node: React.ReactNode; title?: string } {
  switch (id) {
    case 'date':
      return { cls: 'pr-c pr-mono', node: formatShortDate(r.eventDate) };
    case 'description':
      return { cls: 'pr-c pr-desc', node: r.description, title: r.description };
    case 'countries':
      return {
        cls: 'pr-c pr-trunc',
        node: formatCountries(r.affectedCountries),
        title: r.affectedCountries.join(', '),
      };
    case 'subsegments':
      return {
        cls: 'pr-c pr-trunc',
        node: formatSubSegments(r.affectedSubSegments),
        title: r.affectedSubSegments.join(', '),
      };
    case 'category':
      return {
        cls: 'pr-c',
        node: (
          <Badge variant={categoryBadgeVariant(r.pressureCategory)}>
            {SUPER_CATEGORY_LABEL[r.pressureCategory]}
          </Badge>
        ),
      };
    case 'severity':
      return {
        cls: 'pr-c',
        node: <Badge variant={severityBadgeVariant(r.severity)}>{r.severity.toUpperCase()}</Badge>,
      };
    case 'direction':
      return { cls: 'pr-c', node: <DirectionPill direction={r.direction} /> };
    case 'source':
      return { cls: 'pr-c pr-trunc', node: <SourceLink row={r} /> };
  }
}

// ---------- Empty / chrome --------------------------------------------

function EmptyState({
  onClear,
  timeRange,
  setTimeRange,
  totalEvents,
}: {
  onClear: () => void;
  timeRange: TimeRangeKey;
  setTimeRange: (k: TimeRangeKey) => void;
  /** Count of rows in pressure_events that the API returned (before
   *  any client-side filtering). When > 0 it means there ARE events,
   *  the current filters just hide them — surface a one-click escape. */
  totalEvents: number;
}) {
  const { t } = useTranslation();
  const hasHiddenEvents = totalEvents > 0;
  return (
    <div
      style={{
        padding: 'var(--s-4)',
        color: 'var(--text-secondary)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--s-2)',
      }}
    >
      <div style={{ color: 'var(--text-primary)', fontWeight: 500 }}>
        {t('panels.opPressure.empty.headline')}
      </div>
      <div>
        {hasHiddenEvents
          ? t('panels.opPressure.empty.hintWithEvents', {
              count: totalEvents,
              defaultValue:
                '{{count}} event(s) in the database fall outside the current filters. Switch to All time to see them.',
            })
          : t('panels.opPressure.empty.hint')}
      </div>
      <div style={{ display: 'flex', gap: 'var(--s-2)', flexWrap: 'wrap' }}>
        {hasHiddenEvents && timeRange !== 'all' && (
          <button
            type="button"
            onClick={() => setTimeRange('all')}
            style={{ ...emptyActionStyle, fontWeight: 500 }}
            data-action="show-all-time"
          >
            {t('panels.opPressure.empty.showAll', {
              count: totalEvents,
              defaultValue: 'Show all time ({{count}})',
            })}
          </button>
        )}
        {timeRange !== '90d' && timeRange !== 'all' && (
          <button
            type="button"
            onClick={() => setTimeRange('90d')}
            style={emptyActionStyle}
            data-action="extend-time"
          >
            {t('panels.opPressure.empty.extend')}
          </button>
        )}
        <button type="button" onClick={onClear} style={emptyActionStyle}>
          {t('panels.opPressure.filters.clear')}
        </button>
      </div>
    </div>
  );
}

function EmptyMessage({
  children,
  tone,
}: {
  children: React.ReactNode;
  tone?: 'error' | 'neutral';
}) {
  return (
    <div
      style={{
        padding: 'var(--s-4)',
        color: tone === 'error' ? 'var(--color-negative-500)' : 'var(--text-tertiary)',
      }}
    >
      {children}
    </div>
  );
}

function DirectionPill({ direction }: { direction: 'new' | 'rising' | 'easing' }) {
  const variant: 'accent' | 'positive' | 'neutral' =
    direction === 'rising' ? 'accent' : direction === 'easing' ? 'positive' : 'neutral';
  const glyph = direction === 'rising' ? '↑' : direction === 'easing' ? '↓' : '◆';
  const label = direction === 'rising' ? 'Rising' : direction === 'easing' ? 'Easing' : 'New';
  return (
    <Badge variant={variant}>
      {glyph} {label}
    </Badge>
  );
}

function SourceLink({ row }: { row: OperatingPressureRow }) {
  const url = primarySourceUrl(row);
  if (!url) return <span style={{ color: 'var(--text-tertiary)' }}>{EM_DASH}</span>;
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      style={{
        color: 'var(--accent)',
        fontSize: 'var(--t-meta)',
        whiteSpace: 'nowrap',
      }}
    >
      {hostnameOf(url)}
    </a>
  );
}

// ---------- Style snippets --------------------------------------------

const EM_DASH = '—';

// Div-grid table styles — mirror the Screener / Referrals tables: a 22px sticky
// header with a 2px rule on the FT-shade background, --s-2 cell inset, --t-body
// cell text. Description wraps; the metadata columns ellipsis-truncate. Column
// template + scroll width come from the per-panel column config.
const PR_CSS = `
.pr-grid { width:100%; }
.pr-head { position:sticky; top:0; z-index:2; display:grid; height:22px; background:var(--surface-elevated); border-bottom:2px solid var(--text-primary); }
.pr-row { display:grid; align-items:start; border-bottom:1px solid var(--border-subtle); font-size:var(--t-body); }
.pr-row:hover { background:var(--hover-subtle); }
/* overflow:hidden on every cell so a long nowrap value (e.g. a source hostname,
   a badge) is clipped to its track instead of spilling past the column and
   adding phantom horizontal scroll past the table's right edge. */
.pr-c { padding:6px var(--s-2); min-width:0; color:var(--text-primary); font-size:var(--t-body); overflow:hidden; }
.pr-c.pr-trunc, .pr-c.pr-mono { white-space:nowrap; text-overflow:ellipsis; }
.pr-mono { font-family:var(--font-mono, monospace); }
.pr-desc { white-space:normal; line-height:1.35; }
`;

const emptyActionStyle: React.CSSProperties = {
  fontSize: 'var(--t-meta)',
  padding: '4px var(--s-2)',
  border: '1px solid var(--hairline)',
  borderRadius: 2,
  background: 'var(--surface)',
  color: 'var(--text-primary)',
  cursor: 'pointer',
};

// ---------- Pure helpers ----------------------------------------------

function categoryBadgeVariant(sup: SuperCategory): 'accent' | 'positive' | 'neutral' {
  if (sup === 'regulatory') return 'accent';
  if (sup === 'financial') return 'neutral';
  return 'positive';
}

function severityBadgeVariant(s: 'high' | 'medium' | 'low'): 'alert' | 'accent' | 'neutral' {
  if (s === 'high') return 'alert';
  if (s === 'medium') return 'accent';
  return 'neutral';
}

function formatShortDate(iso: string | null): string {
  if (!iso) return EM_DASH;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return EM_DASH;
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
}

// Multi-value cell formatters — matched to the Screener panel:
//   - Country: raw 2-letter code ("GB"). Op-Pressure events can affect
//     multiple markets, so codes are joined with " · ".
//   - Sub-segment: human-readable label in sentence case, like Screener's
//     "Community pharmacy". The Op-Pressure backend emits a different
//     taxonomy than @hc/shared's SubSegment enum (e.g. dental_general,
//     mental_health_services, primary_care) — none of which match
//     SUB_SEGMENT_LABELS' keys — so we humanise the snake_case codes
//     instead of a lookup. Replacing underscores with spaces and upper-
//     casing the first character gives "Mental health services" /
//     "Community pharmacy" — the same style Screener serves pre-labelled.
// The cell uses tdTrunc to ellipsis-truncate when the list overflows;
// the full list stays readable via the cell's title attribute.

function humanizeSubSegment(code: string): string {
  if (!code) return code;
  const spaced = code.replace(/_/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

function formatCountries(codes: string[]): string {
  if (codes.length === 0) return EM_DASH;
  return codes.join(' · ');
}

function formatSubSegments(segs: string[]): string {
  if (segs.length === 0) return EM_DASH;
  return segs.map(humanizeSubSegment).join(' · ');
}

function primarySourceUrl(row: OperatingPressureRow): string | null {
  return row.sources[0]?.sourceUrl ?? null;
}

function hostnameOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

// Sort value per column + a null-last comparator (numbers numeric, text by
// locale). Date sorts by timestamp, severity by a high→low rank; multi-value
// cells (countries / sub-segments) sort by their first entry.
const SEVERITY_RANK: Record<string, number> = { high: 3, medium: 2, low: 1 };

function pressureSortValue(id: PressureColumnId, r: OperatingPressureRow): number | string | null {
  switch (id) {
    case 'date':
      return r.eventDate ? Date.parse(r.eventDate) : null;
    case 'description':
      return r.description;
    case 'countries':
      return r.affectedCountries[0] ?? null;
    case 'subsegments':
      return r.affectedSubSegments[0] ? humanizeSubSegment(r.affectedSubSegments[0]) : null;
    case 'category':
      return SUPER_CATEGORY_LABEL[r.pressureCategory];
    case 'severity':
      return SEVERITY_RANK[r.severity] ?? null;
    case 'direction':
      return r.direction;
    case 'source':
      return hostnameOf(primarySourceUrl(r) ?? '') || null;
  }
}

function cmpPressure(
  a: number | string | null,
  b: number | string | null,
  dir: 'asc' | 'desc',
): number {
  const an = a == null || a === '';
  const bn = b == null || b === '';
  if (an && bn) return 0;
  if (an) return 1; // nulls/blanks always sort last, regardless of direction
  if (bn) return -1;
  const r =
    typeof a === 'number' && typeof b === 'number' ? a - b : String(a).localeCompare(String(b));
  return dir === 'asc' ? r : -r;
}
